import { useEffect } from "react";

const SITE = "https://yourelitehomecare.com";
const BRAND = "Elite Home Care by Hortense";

function defaultOgImage(path) {
  if (!path || path === "/") return "/og/home.webp";
  const seg = path.split("/").filter(Boolean);
  if (seg[0] === "services" && seg[1] && seg[2]) return `/og/money-${seg[1]}-${seg[2]}.webp`;
  if (seg[0] === "services" && seg[1]) return `/og/service-${seg[1]}.webp`;
  if (seg[0] === "home-care" && seg[1]) return `/og/city-${seg[1]}.webp`;
  if (seg[0] === "blog" && seg[1]) return `/og/blog-${seg[1]}.webp`;
  if (seg[0] === "blog") return "/og/blog.webp";
  if (seg[0] === "services") return "/og/services.webp";
  if (seg[0] === "about") return "/og/about.webp";
  if (seg[0] === "contact") return "/og/contact.webp";
  if (seg[0] === "directory") return "/og/directory.webp";
  return "/og/default.webp";
}

/**
 * Imperative SEO: directly manages meta tags in <head>.
 * Guarantees a single, correct set of SEO tags per route (no duplicates)
 * because we mutate the SAME nodes on every navigation instead of
 * appending new ones like react-helmet-async does in this SPA.
 *
 * Each managed tag carries data-seo="1" so we can find or create it.
 */
function upsertMeta({ selector, tagName, attrs }) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function removeManaged(selector) {
  document.head
    .querySelectorAll(`${selector}[data-seo="1"]`)
    .forEach((n) => n.remove());
}

export default function Seo({
  title,
  description,
  path = "/",
  image,
  jsonLd = null,
}) {
  const fullTitle = title
    ? `${title} | ${BRAND}`
    : `${BRAND} | Premium In-Home Senior Care in Southwest Florida`;
  const url = `${SITE}${path}`;
  const chosen = image || defaultOgImage(path);
  const imgUrl = chosen.startsWith("http") ? chosen : `${SITE}${chosen}`;

  useEffect(() => {
    // Title
    document.title = fullTitle;

    // Description (mutate the single existing tag from index.html)
    let descEl = document.head.querySelector('meta[name="description"]');
    if (!descEl) {
      descEl = document.createElement("meta");
      descEl.setAttribute("name", "description");
      document.head.appendChild(descEl);
    }
    descEl.setAttribute("content", description);

    // Canonical (single tag, always the current URL)
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Open Graph
    upsertMeta({ selector: 'meta[property="og:type"]', tagName: "meta", attrs: { property: "og:type", content: "website" } });
    upsertMeta({ selector: 'meta[property="og:site_name"]', tagName: "meta", attrs: { property: "og:site_name", content: BRAND } });
    upsertMeta({ selector: 'meta[property="og:title"]', tagName: "meta", attrs: { property: "og:title", content: fullTitle } });
    upsertMeta({ selector: 'meta[property="og:description"]', tagName: "meta", attrs: { property: "og:description", content: description } });
    upsertMeta({ selector: 'meta[property="og:url"]', tagName: "meta", attrs: { property: "og:url", content: url } });
    upsertMeta({ selector: 'meta[property="og:image"]', tagName: "meta", attrs: { property: "og:image", content: imgUrl } });
    upsertMeta({ selector: 'meta[property="og:locale"]', tagName: "meta", attrs: { property: "og:locale", content: "en_US" } });

    // Twitter
    upsertMeta({ selector: 'meta[name="twitter:card"]', tagName: "meta", attrs: { name: "twitter:card", content: "summary_large_image" } });
    upsertMeta({ selector: 'meta[name="twitter:title"]', tagName: "meta", attrs: { name: "twitter:title", content: fullTitle } });
    upsertMeta({ selector: 'meta[name="twitter:description"]', tagName: "meta", attrs: { name: "twitter:description", content: description } });
    upsertMeta({ selector: 'meta[name="twitter:image"]', tagName: "meta", attrs: { name: "twitter:image", content: imgUrl } });

    // Per-page JSON-LD (mount a single tag; replace on nav)
    removeManaged('script[data-seo-jsonld="1"]');
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [fullTitle, description, url, imgUrl, jsonLd]);

  return null;
}
